import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormField,
  Input,
  Modal,
  Select,
  SpaceBetween,
} from '@cloudscape-design/components';
import { PartTypes, PPartStatus } from '@parallel-fluidics/shared/constants';
import { ProductStatus, productVersionSchema } from '@parallel-fluidics/shared/models/products';
import { getPPartId } from '@parallel-fluidics/shared/models/projects/parts';
import FileUploader from 'components/FileUploader';
import getPPartOption from 'features/pPart/utils/getPPartOption';
import createProductVersion from 'features/products/apis/createProductVersion';
import updateProductVersion from 'features/products/apis/updateProductVersion';
import { setIn, useFormik } from 'formik';
import { useGetProjectParts } from 'hooks/use-basicQueries';
import useProjects from 'hooks/use-projects';
import PropTypes from 'prop-types';
import { useUserData } from 'providers/userDataProvider';
import React, { useMemo, useState } from 'react';

const VERSION_REGEX = /^[A-Z]+$/;

function inputValue(value) {
  return typeof value === 'number' ? String(value) : '';
}

function numberOrNull(value) {
  return value === '' || value == null ? null : Number(value);
}

function buildInitialValues(initVersion) {
  const reorderPolicy = initVersion?.reorderPolicy || {};

  return {
    id: initVersion?.id || 'A',
    status: initVersion?.status || ProductStatus.DRAFT,
    projectId: initVersion?.projectId || '',
    projectDocId: initVersion?.projectDocId || '',
    pPartId: initVersion?.pPartId || '',
    pPartDocId: initVersion?.pPartDocId || '',
    unitPrice: inputValue(initVersion?.unitPrice),
    leadtimeDays: inputValue(initVersion?.leadtimeDays),
    previewGlb: initVersion?.previewGlb || [],
    reorderPolicy: {
      enabled: Boolean(reorderPolicy.enabled),
      triggerQuantity: inputValue(reorderPolicy.triggerQuantity),
      targetLevel: inputValue(reorderPolicy.targetLevel),
      updatedAt: reorderPolicy.updatedAt || null,
      updatedBy: reorderPolicy.updatedBy || null,
    },
    activateNow: false,
  };
}

function buildReorderPolicy(values, user) {
  const { reorderPolicy = {} } = values;
  const enabled = Boolean(reorderPolicy.enabled);

  return {
    enabled,
    triggerQuantity: enabled ? numberOrNull(reorderPolicy.triggerQuantity) : null,
    targetLevel: enabled ? numberOrNull(reorderPolicy.targetLevel) : null,
    updatedAt: reorderPolicy.updatedAt || null,
    updatedBy: { uid: user?.id, email: user?.email },
  };
}

function buildVersionPayload(values, user) {
  return {
    id: values.id.trim().toUpperCase(),
    projectId: values.projectId,
    projectDocId: values.projectDocId,
    pPartId: values.pPartId,
    pPartDocId: values.pPartDocId || null,
    unitPrice: numberOrNull(values.unitPrice),
    leadtimeDays: numberOrNull(values.leadtimeDays),
    previewGlb: values.previewGlb?.length ? values.previewGlb : null,
    reorderPolicy: buildReorderPolicy(values, user),
  };
}

function getValidationError(errors, path) {
  return path.split('.').reduce((value, key) => value?.[key], errors);
}

function getReorderPolicyError(errors) {
  return typeof errors.reorderPolicy === 'string' ? errors.reorderPolicy : '';
}

export default function CreateProductVersionModal({
  open,
  onClose,
  onSuccess,
  product,
  isEditing = false,
  initVersion = null,
}) {
  const { user } = useUserData();
  const [submitError, setSubmitError] = useState('');

  const {
    projectOptions: internalProjectOptions,
    isLoading: isLoadingInternalProjects,
  } = useProjects({ isInternal: true });

  const canEditManufacturingFields = !isEditing || initVersion?.status === ProductStatus.DRAFT;
  const initialValues = useMemo(() => buildInitialValues(initVersion), [initVersion]);

  const {
    values,
    errors,
    isSubmitting,
    handleSubmit,
    setFieldValue,
  } = useFormik({
    initialValues,
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    validate: (validateValues) => {
      let validationErrors = {};
      const versionId = validateValues.id?.trim().toUpperCase();

      if (!versionId || !VERSION_REGEX.test(versionId)) {
        validationErrors = setIn(
          validationErrors,
          'id',
          'Version must contain only uppercase letters (e.g. A, B, AA)',
        );
      }

      const payload = buildVersionPayload(validateValues, user);

      try {
        productVersionSchema.validateSync({
          ...payload,
          status: validateValues.activateNow ? ProductStatus.ACTIVE : validateValues.status,
          createdBy: initVersion?.createdBy || {
            uid: user?.id,
            email: user?.email,
          },
        }, { abortEarly: false });
      } catch (error) {
        error.inner?.forEach((validationError) => {
          if (
            validationError.path
            && !getValidationError(validationErrors, validationError.path)
          ) {
            validationErrors = setIn(
              validationErrors,
              validationError.path,
              validationError.message,
            );
          }
        });
      }

      if (canEditManufacturingFields && !validateValues.projectId) {
        validationErrors = setIn(validationErrors, 'projectId', 'Select an internal project');
      }
      if (canEditManufacturingFields && !validateValues.pPartDocId) {
        validationErrors = setIn(validationErrors, 'pPartId', 'Select a product part');
      }

      return validationErrors;
    },
    onSubmit: async (submitValues) => {
      try {
        setSubmitError('');
        const versionPayload = buildVersionPayload(submitValues, user);
        const { id: versionId } = versionPayload;

        if (isEditing) {
          await updateProductVersion({
            productDocId: product.id,
            versionId,
            versionData: versionPayload,
          });
        } else {
          await createProductVersion({
            productDocId: product.id,
            versionId,
            versionData: versionPayload,
            activateNow: submitValues.activateNow,
            createdBy: {
              uid: user?.id,
              email: user?.email,
            },
          });
        }
        onSuccess();
        onClose();
      } catch (error) {
        setSubmitError(error.message || 'Unable to save product version');
      }
    },
  });

  const selectedProject = useMemo(() => {
    if (!values?.projectDocId || !internalProjectOptions?.length) return null;
    return internalProjectOptions.find((option) => option.id === values.projectDocId);
  }, [internalProjectOptions, values?.projectDocId]);

  const {
    data: productParts = [],
    isLoading: isLoadingProductParts,
  } = useGetProjectParts({
    projectDocId: selectedProject?.id,
    status: PPartStatus.ACTIVE,
    type: PartTypes.PRODUCT,
  });

  const pPartOptions = useMemo(
    () => productParts.map((part) => ({
      ...getPPartOption(part),
      pPartId: getPPartId(part),
    })),
    [productParts],
  );

  const modalVerb = isEditing ? 'Edit' : 'Create';
  const reorderPolicyError = getReorderPolicyError(errors);

  return (
    <Modal
      visible={open}
      onDismiss={onClose}
      header={`${modalVerb} version for ${product.sku}`}
    >
      <form onSubmit={handleSubmit}>
        <SpaceBetween direction="vertical" size="s">
          {submitError ? (
            <Alert key="submit-error" type="error">{submitError}</Alert>
          ) : null}

          <FormField label="Version ID" errorText={errors.id}>
            <Input
              value={values.id}
              disabled={isEditing}
              onChange={({ detail }) => {
                setFieldValue('id', detail.value.toUpperCase());
              }}
            />
          </FormField>

          <FormField label="Internal project" errorText={errors.projectId}>
            <Select
              filteringType="auto"
              disabled={!canEditManufacturingFields}
              statusType={isLoadingInternalProjects ? 'loading' : 'finished'}
              selectedOption={selectedProject}
              options={internalProjectOptions}
              onChange={({ detail }) => {
                setFieldValue('projectDocId', detail.selectedOption.id);
                setFieldValue('projectId', detail.selectedOption.value);
                setFieldValue('pPartId', '');
                setFieldValue('pPartDocId', '');
              }}
            />
          </FormField>

          <FormField label="Product part" errorText={errors.pPartId}>
            <Select
              filteringType="auto"
              disabled={!canEditManufacturingFields}
              statusType={isLoadingProductParts ? 'loading' : 'finished'}
              selectedOption={
                pPartOptions.find((option) => option.value === values.pPartDocId)
                || (values.pPartId ? { value: values.pPartId, label: values.pPartId } : null)
              }
              options={pPartOptions}
              empty="Select an internal project first"
              onChange={({ detail }) => {
                setFieldValue('pPartId', detail.selectedOption.pPartId);
                setFieldValue('pPartDocId', detail.selectedOption.value);
              }}
            />
          </FormField>

          <FormField label="Unit price" errorText={errors.unitPrice}>
            <Input
              type="number"
              inputMode="decimal"
              step={0.01}
              disabled={!canEditManufacturingFields}
              value={values.unitPrice}
              onChange={({ detail }) => setFieldValue('unitPrice', detail.value)}
            />
          </FormField>

          <FormField label="Lead time (days)" errorText={errors.leadtimeDays}>
            <Input
              type="number"
              inputMode="numeric"
              disabled={!canEditManufacturingFields}
              value={values.leadtimeDays}
              onChange={({ detail }) => setFieldValue('leadtimeDays', detail.value)}
            />
          </FormField>

          <FormField label="Reorder policy" errorText={reorderPolicyError}>
            <Checkbox
              checked={values.reorderPolicy.enabled}
              onChange={({ detail }) => setFieldValue('reorderPolicy.enabled', detail.checked)}
            >
              Enabled
            </Checkbox>
          </FormField>

          <FormField
            label="Trigger quantity"
            errorText={getValidationError(errors, 'reorderPolicy.triggerQuantity')}
          >
            <Input
              type="number"
              inputMode="numeric"
              disabled={!values.reorderPolicy.enabled}
              value={values.reorderPolicy.triggerQuantity}
              onChange={({ detail }) => (
                setFieldValue('reorderPolicy.triggerQuantity', detail.value)
              )}
            />
          </FormField>

          <FormField
            label="Target level"
            errorText={getValidationError(errors, 'reorderPolicy.targetLevel')}
          >
            <Input
              type="number"
              inputMode="numeric"
              disabled={!values.reorderPolicy.enabled}
              value={values.reorderPolicy.targetLevel}
              onChange={({ detail }) => setFieldValue('reorderPolicy.targetLevel', detail.value)}
            />
          </FormField>

          <FormField
            label="3D Preview — upload GLB directly"
            description="Please prepare the GLB file and upload it here."
          >
            <FileUploader
              uploadPath={`products/${product.id}/previews/${values.id}`}
              uploadedFiles={values.previewGlb || []}
              uploadErrors={[]}
              accept=".glb"
              constraintText="Accepted format: .glb — uploaded file will be used after saving"
              fileTypeName="3D Preview"
              onEmpty={() => setFieldValue('previewGlb', [])}
              onUpload={(files) => setFieldValue('previewGlb', files)}
            />
          </FormField>

          {!isEditing && (
            <Checkbox
              key="activate-now"
              checked={values.activateNow}
              onChange={({ detail }) => setFieldValue('activateNow', detail.checked)}
            >
              Activate immediately
            </Checkbox>
          )}

          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" formAction="none" onClick={onClose}>Cancel</Button>
              <Button
                variant="primary"
                type="submit"
                loading={isSubmitting}
              >
                {`${modalVerb} version`}
              </Button>
            </SpaceBetween>
          </Box>
        </SpaceBetween>
      </form>
    </Modal>
  );
}

CreateProductVersionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  product: PropTypes.shape({
    id: PropTypes.string.isRequired,
    sku: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  isEditing: PropTypes.bool,
  initVersion: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
    projectId: PropTypes.string,
    projectDocId: PropTypes.string,
    pPartId: PropTypes.string,
    pPartDocId: PropTypes.string,
    unitPrice: PropTypes.number,
    leadtimeDays: PropTypes.number,
    previewGlb: PropTypes.arrayOf(PropTypes.shape({
      downloadURL: PropTypes.string,
      lastModifiedTS: PropTypes.number,
      name: PropTypes.string,
      ref: PropTypes.string,
      type: PropTypes.string,
      size: PropTypes.number,
    })),
    reorderPolicy: PropTypes.shape({
      enabled: PropTypes.bool,
      triggerQuantity: PropTypes.number,
      targetLevel: PropTypes.number,
    }),
  }),
};
